import { useEffect, useState } from 'react'
import { List, ListItemButton, ListItemIcon, ListItemText } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { globalHistory } from '@reach/router'
import iconPatient from '../../assets/new-left-menu/patients.svg'
import iconStadistics from '../../assets/new-left-menu/statistics.svg'
import iconForms from '../../assets/new-left-menu/forms.svg'

import styles from './ListItems.module.css'
import { ILoggedUserService } from '../../modules/users/services/LoggedUserService'
import { LOGGED_USER_SERVICE_KEY } from '../../modules/users'
import { getUserContainer } from '../../container/user-module'
import {
  ROUTE_FORM_GENERATOR,
  ROUTE_PATIENT_FORMS,
  ROUTE_PATIENTS,
  ROUTE_PROTOCOL_NEW,
  ROUTE_PROTOCOLS,
} from 'routes/routes-constants'

import { useUserRoles } from 'modules/users/hooks/useUserRoles'

interface ItemProps {
  url: string
  label: string
  handle: string
  icon: string
  alt: string
  visible: boolean
  // Cuando dos entradas comparten url (Protocolos y Gestion de protocolos) no
  // se puede decidir cual va marcada mirando solo la url, asi que cada una
  // puede traer su propia regla.
  match?: (pathname: string) => boolean
}

// Por defecto: la ruta actual es la del item o cuelga de ella.
// Antes era `item.url.includes(pathname)`, con los operandos al reves: en el
// detalle de un protocolo no se marcaba ninguna entrada y en "/" se marcaban
// todas, porque cualquier url contiene "/".
const defaultMatch = (url: string) => (pathname: string) =>
  pathname === url || pathname.startsWith(`${url}/`)

type ListItemsProps = {
  handleClick: (type: string) => void
  isOpen: boolean
  setOpen: (b: boolean) => void
}

const userContainer = getUserContainer()
const loggedUserService = userContainer.get<ILoggedUserService>(LOGGED_USER_SERVICE_KEY)

const ListItems = (props: ListItemsProps) => {
  const { t } = useTranslation()
  const loggedUser = loggedUserService.get()
  // globalHistory and not useLocation: the menu mounts in CustomDrawer, outside
  // the <Router>, where there is no LocationContext and the hook throws.
  const [pathname, setPathname] = useState<string>(window.location.pathname)

  useEffect(() => globalHistory.listen(({ location }) => setPathname(location.pathname)), [])

  const { isPatient, canViewPatients, canAccessForms, canManageProtocols } =
    useUserRoles(loggedUser)

  // Menu tal como lo pide diseno: Protocols, Protocol Management, Users y
  // Questionnaires. Start y Statistics ya no aparecen; sus rutas siguen vivas y
  // se puede llegar a ellas por URL, pero no por el menu.
  //
  // "Protocol Management" apunta a la lista de protocolos: en la app es el
  // detalle de un protocolo concreto y necesita un id, asi que no puede ser un
  // enlace fijo. Desde la lista se entra al detalle.
  const ITEMS: ItemProps[] = [
    {
      label: t('protocols'),
      url: ROUTE_PROTOCOLS,
      handle: 'PROTOCOLS',
      icon: iconForms,
      alt: 'Protocols Icon',
      visible: canManageProtocols,
      // Solo la lista, no el detalle
      match: (p) => p === ROUTE_PROTOCOLS,
    },
    {
      label: t('protocolManagement'),
      url: ROUTE_PROTOCOL_NEW,
      handle: 'PROTOCOL-MANAGEMENT',
      icon: iconStadistics,
      alt: 'Protocol Management Icon',
      visible: canManageProtocols,
      // Tanto crear un protocolo como editar uno existente son esta vista
      match: (p) => p.startsWith(`${ROUTE_PROTOCOLS}/`),
    },
    {
      label: t('usersTitle'),
      url: ROUTE_PATIENTS,
      handle: 'PATIENTS',
      icon: iconPatient,
      alt: 'Users Icon',
      visible: canViewPatients,
    },
    {
      label: t('questionnaires'),
      url: isPatient ? ROUTE_PATIENT_FORMS : ROUTE_FORM_GENERATOR,
      handle: 'FORM-GENERATOR',
      icon: iconForms,
      alt: 'Questionnaires Icon',
      visible: canAccessForms,
    },
  ]

  return (
    <List className={styles.listContainer}>
      {ITEMS.filter((item) => item.visible).map((item: ItemProps) => {
        const isActive = (item.match ?? defaultMatch(item.url))(pathname)

        return (
          <ListItemButton
            key={item.label}
            className={styles.listItem}
            onClick={() => props.handleClick(item.handle)}
          >
            <ListItemIcon className={styles.iconLayer}>
              <img
                className={`${styles.icon} ${isActive && styles.iconActive}`}
                src={item.icon}
                alt={item.alt}
              />
            </ListItemIcon>
            <ListItemText
              className={`${styles.text} ${isActive && styles.textActive}`}
              primary={item.label}
            />
          </ListItemButton>
        )
      })}
    </List>
  )
}

export default ListItems
